import * as React from 'react';
import { useIntl } from 'react-intl';
import {
  CmdAlert,
  CmdCheckboxGroup,
  CmdInput,
  CmdLabel,
  CmdModalFooterButton,
  CmdSimpleSelect,
  CmdSimpleSelectOption,
  CmdSubpage,
  CmdSwitch,
  CmdTextarea,
} from '@commander-services/gui-components';
import CmdBox from '@commander-services/cmd-box';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { useQueries } from '@tanstack/react-query';
import { Controller, useForm } from 'react-hook-form';
import FormikValidatorService, {
  IValidationApiResponse,
} from '../../Services/FormikValidatorService';
import PermissionsService, {
  PERMISSION_GDPR_PROFILE_LIST,
} from '../../Services/PermissionsService';
import ConfirmDialog from '../ConfirmDialog';
import { IToastr } from '../Toastr/ToastrState';
import * as ToastrState from '../Toastr/ToastrState';
import OnlineHelp from '../OnlineHelp';
import { customersAtom, ICustomer, selectedCustomersAtom } from '../../store/recoil/customers';
import { add, edit, getCustomers, getData, getValidation, IAuthItem } from './GdprProfileService';
import { IGdprProfileInputForm } from './types';
import { gdprSettingsAtom, setLoaderAtom } from '../../store/recoil/AppState';
import { transformErrors } from '../../Services/HookFormService';
import useAnalytics from '../../hooks/useAnalytics';

interface FormProps {
  isEdited: boolean;
  closeCallback: () => void;
  editingItemId: number | null;
  updateTable?: (() => void) | undefined;
  customerId?: number | null;
  usersCount?: number;
}

export default function GdprProfileForm(props: FormProps) {
  const { trackEvent } = useAnalytics();
  const { formatMessage: f } = useIntl();
  const [validation, setValidation] = React.useState<IValidationApiResponse>({});
  const [loader, setLoader] = useRecoilState<boolean>(setLoaderAtom);
  const [dataReadyToSubmit, setDataReadyToSubmit] = React.useState<IGdprProfileInputForm | null>(
    null
  );
  const [isCancelButtonClicked, setIsCancelButtonClicked] = React.useState<boolean>(false);
  const toastr = useRecoilValue<IToastr | null>(ToastrState.toastr);
  const [editingData, setEditingData] = React.useState<IGdprProfileInputForm | null>(null);
  const [customerOptions, setCustomerOptions] = React.useState<CmdSimpleSelectOption[] | null>(
    null
  );
  const customers = useRecoilValue<ICustomer[]>(customersAtom);
  const selectedCustomers = useRecoilValue<number[]>(selectedCustomersAtom);
  const setGdprSettings = useSetRecoilState<Record<string, any>>(gdprSettingsAtom);

  const validateFields = (values) => {
    let formErrors = {};
    if (!validation) {
      return formErrors;
    }
    formErrors = FormikValidatorService.validateByValidationApiResponse(values, validation);
    return transformErrors(formErrors);
  };

  const {
    handleSubmit,
    watch,
    control,
    setValue,
    setError,
    trigger,
    formState: { errors },
  } = useForm<IGdprProfileInputForm>({
    mode: 'onChange',
    defaultValues: {
      name: '',
      customerId: '',
      description: '',
      map: 0,
      R01: 0,
      R03: 0,
      R05: 0,
      R06: 0,
      R07: 0,
      correction: 0,
      ride: 0,
      rideDetail: [],
      R05Detail: [],
      refueling: 0,
    },
    resolver: async (data) => {
      const formErrors = validateFields(data);
      return {
        values: formErrors && Object.keys(formErrors).length ? {} : data, // if there's an error, return empty values
        errors: formErrors,
      };
    },
  });

  const handleConfirmKeepSubpage = () => {
    trackEvent(['GDPR profile form', 'click', 'Keep on subpage']);
    setIsCancelButtonClicked(false);
  };

  const handleConfirmCancelAction = () => {
    trackEvent(['GDPR profile form', 'click', 'Cancel confirm submit']);
    setDataReadyToSubmit(null);
  };

  const onSubmit = async (values: IGdprProfileInputForm) => {
    if (props.editingItemId && props.customerId && props.usersCount && !dataReadyToSubmit) {
      setDataReadyToSubmit(values);
    } else {
      const newValues: any = {};
      if (!values.customerId) {
        newValues.customerId =
          customerOptions && customerOptions[0] ? customerOptions[0].value : null;
      }
      newValues.name = values.name;
      newValues.customerId = values.customerId;
      newValues.description = values.description;
      newValues.authMap = [
        {
          name: 'maps',
          enabled: String(values.map) === '1',
        },
      ];
      newValues.authReports = [
        {
          name: 'R01',
          enabled: String(values.R01) === '1',
        },
        {
          name: 'R03',
          enabled: String(values.R03) === '1',
        },
        {
          name: 'R05',
          enabled: String(values.R05) === '1',
          can: {
            showPrivateData:
              String(values.R05) === '1' && values.R05Detail.includes('showPrivateData'),
          },
        },
        {
          name: 'R06',
          enabled: String(values.R06) === '1',
        },
        {
          name: 'R07',
          enabled: String(values.R07) === '1',
        },
      ];
      newValues.authCorrections = [
        {
          name: 'corrections',
          enabled: String(values.correction) === '1',
        },
      ];

      newValues.authRefuelings = [
        {
          name: 'refuelings',
          enabled: String(values.refueling) === '1',
        },
      ];
      newValues.authRides = [
        {
          name: 'rideEnd',
          enabled: String(values.ride) === '1',
          can: {
            showPrivateData:
              String(values.ride) === '1' && values.rideDetail.includes('showPrivateData'),
            showEvents: String(values.ride) === '1' && values.rideDetail.includes('showEvents'),
            showAlarms: String(values.ride) === '1' && values.rideDetail.includes('showAlarms'),
            showWaypoints:
              String(values.ride) === '1' && values.rideDetail.includes('showWaypoints'),
            changeDriver: String(values.ride) === '1' && values.rideDetail.includes('changeDriver'),
            showRideDetail:
              String(values.ride) === '1' && values.rideDetail.includes('showRideDetail'),
            showRide: String(values.ride) === '1' && values.rideDetail.includes('showRide'),
            changeRidePrivacy:
              String(values.ride) === '1' && values.rideDetail.includes('changeRidePrivacy'),
          },
        },
      ];
      setLoader(true);
      const response = props.editingItemId
        ? await edit(props.editingItemId, newValues)
        : await add(newValues);
      setLoader(false);

      if (props.editingItemId) {
        trackEvent(['GDPR profile form', 'submit', 'Update']);
      } else {
        trackEvent(['GDPR profile form', 'submit', 'Create']);
      }

      if (response && (response.status === 200 || response.status === 'ok') && props.updateTable) {
        props.closeCallback();
        props.updateTable();
        const responseProfile = response?.profile || response.data.profile;
        const profiles = localStorage.getItem('gdprProfiles');
        if (profiles) {
          const profilesArr = JSON.parse(profiles).map((i) => {
            if (
              String(i.customerId) === String(responseProfile?.customerId) &&
              String(i.id) === String(responseProfile?.id)
            ) {
              return response;
            }
            return i;
          });
          localStorage.setItem('gdprProfiles', JSON.stringify(profilesArr));
          setGdprSettings(profilesArr);
        }
      }
      if (response && response.status === 400 && response.data.errors) {
        // const newErrors = {};
        Object.keys(response.data.errors).forEach((key: string) => {
          if (response.data.errors && response.data.errors[key]) {
            const newError = response.data.errors[key]
              .map((errorMessage: string) => f({ id: errorMessage }))
              .join('<br/>');
            setError(key, { message: newError });
          }
        });
      }
    }
  };

  const handleConfirmSubmitAction = () => {
    trackEvent(['GDPR profile form', 'click', 'Confirm submit']);
    if (dataReadyToSubmit) {
      onSubmit(dataReadyToSubmit);
    }
  };

  const handleConfirmCancelSubpage = () => {
    trackEvent(['GDPR profile form', 'click', 'Confirm close subpage']);
    props.closeCallback();
  };

  const closeSubpage = () => {
    trackEvent(['GDPR profile form', 'click', 'Close subpage']);
    setIsCancelButtonClicked(true);
  };

  const getValidationData = async () => {
    setLoader(true);
    const response = await getValidation();
    if (response) {
      setValidation(response);
    }
    setLoader(false);
  };

  const getCustomersData = async () => {
    const response = await getCustomers();

    if (response) {
      setCustomerOptions(response);
      if (response.length === 1) {
        setValue('customerId', response[0].value);
        trigger('customerId');
      }
    }
  };

  const getCustomerLabel = () => {
    if (editingData) {
      const customer = customers.find(
        (item: ICustomer) => String(item.id) === String(editingData.customerId)
      );
      if (customer) {
        return customer.name;
      }
    }
    if (selectedCustomers.length === 1) {
      const customer = customers.find((item: ICustomer) => selectedCustomers.includes(item.id));
      if (customer) {
        return customer.name;
      }
    }
    if (customerOptions?.length === 1) {
      const customer = customers.find(
        (item: ICustomer) => String(customerOptions?.[0].value) === String(item.id)
      );
      if (customer) {
        return customer.name;
      }
    }
    return '';
  };

  const getGdprProfile = async (id: number) => {
    setLoader(true);

    const response = await getData(id);

    if (response) {
      let R01: IAuthItem | undefined | null = null;
      let R03: IAuthItem | undefined | null = null;
      let R05: IAuthItem | undefined | null = null;
      let R06: IAuthItem | undefined | null = null;
      let R07: IAuthItem | undefined | null = null;
      if (response.authReports) {
        R01 = Object.values(response.authReports).find((i) => i.name === 'R01');
        R03 = Object.values(response.authReports).find((i) => i.name === 'R03');
        R05 = Object.values(response.authReports).find((i) => i.name === 'R05');
        R06 = Object.values(response.authReports).find((i) => i.name === 'R06');
        R07 = Object.values(response.authReports).find((i) => i.name === 'R07');
      }
      const rideDetail: string[] = [];
      if (response.authRides && response.authRides[0] && response.authRides[0].can) {
        Object.keys(response.authRides[0].can).forEach((key: string) => {
          if (response.authRides[0].can[key] === true) {
            rideDetail.push(key);
          }
        });
      }

      const R05Detail: string[] = [];
      if (R05 !== undefined && R05 !== null && R05.enabled && R05.can) {
        Object.keys(R05.can).forEach((key: string) => {
          if (R05?.can[key] === true) {
            R05Detail.push(key);
          }
        });
      }

      const inputs: IGdprProfileInputForm = {
        name: response.name,
        customerId: String(response.customerId),
        description: response.description,
        map:
          response.authMap && response.authMap[0]
            ? response.authMap[0].enabled === true
              ? 1
              : 0
            : 0,
        R01: R01 ? (R01.enabled === true ? 1 : 0) : 0,
        R03: R03 ? (R03.enabled === true ? 1 : 0) : 0,
        R05: R05 ? (R05.enabled === true ? 1 : 0) : 0,
        R06: R06 ? (R06.enabled === true ? 1 : 0) : 0,
        R07: R07 ? (R07.enabled === true ? 1 : 0) : 0,
        R05Detail,
        correction:
          response.authCorrections && response.authCorrections[0]
            ? response.authCorrections[0].enabled === true
              ? 1
              : 0
            : 0,
        ride:
          response.authRides && response.authRides[0]
            ? response.authRides[0].enabled === true
              ? 1
              : 0
            : 0,
        rideDetail,
        refueling:
          response.authRefuelings && response.authRefuelings[0]
            ? response.authRefuelings[0].enabled === true
              ? 1
              : 0
            : 0,
      };
      setEditingData(inputs);
    }
    setLoader(false);
  };

  useQueries({
    queries: [
      {
        queryKey: ['gdprProfileValidation', props.editingItemId, selectedCustomers],
        queryFn: () => getValidationData(),
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        enabled: PermissionsService.hasAccess(
          PERMISSION_GDPR_PROFILE_LIST,
          ['create', 'update'],
          selectedCustomers
        ),
      },
      {
        queryKey: ['gdprProfileCustomers', props.editingItemId, selectedCustomers],
        queryFn: () => getCustomersData(),
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        enabled: PermissionsService.hasAccess(
          PERMISSION_GDPR_PROFILE_LIST,
          ['create', 'update'],
          selectedCustomers
        ),
      },
      {
        queryKey: ['gdprProfileData', props.editingItemId, selectedCustomers],
        queryFn: () => getGdprProfile(props.editingItemId || 0),
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        enabled: !!props.editingItemId,
      },
    ],
  });

  React.useEffect(() => {
    if (!props.editingItemId && toastr && toastr.message === 'toast.forms.checkInputFields') {
      trigger('name');
      setError('name', { message: f({ id: 'form.rules.gdpr.uniqueProfileName' }) });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toastr]);

  React.useEffect(() => {
    if (errors.name?.message === f({ id: 'form.rules.gdpr.uniqueProfileName' })) {
      setError('name', { message: '' });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watch('name')]);

  React.useEffect(() => {
    if (editingData) {
      setValue('name', editingData.name);
      setValue('customerId', editingData.customerId);
      setValue('description', editingData.description);
      setValue('map', editingData.map);
      setValue('R01', editingData.R01);
      setValue('R03', editingData.R03);
      setValue('R05', editingData.R05);
      setValue('R06', editingData.R06);
      setValue('R07', editingData.R07);
      setValue('correction', editingData.correction);
      setValue('ride', editingData.ride);
      setValue('rideDetail', editingData.rideDetail);
      setValue('R05Detail', editingData.R05Detail);
      setValue('refueling', editingData.refueling);
    }
  }, [editingData, setValue]);

  const isDisabledSwitch = !(
    (props.editingItemId &&
      props.customerId &&
      PermissionsService.hasAccess(PERMISSION_GDPR_PROFILE_LIST, ['update'], props.customerId)) ||
    !props.editingItemId
  );

  const subpageButtons: CmdModalFooterButton[] =
    (props.editingItemId &&
      props.customerId &&
      PermissionsService.hasAccess(PERMISSION_GDPR_PROFILE_LIST, ['update'], props.customerId)) ||
    (!props.editingItemId &&
      PermissionsService.hasAccess(PERMISSION_GDPR_PROFILE_LIST, ['create'], props.customerId))
      ? [
          {
            id: 'gdpr-profile-form-close',
            type: 'button',
            className: 'e-button',
            title: f({ id: 'suppliersForm.close' }),
            closeCallback: closeSubpage,
          },
          {
            id: 'gdpr-profile-form-submit',
            type: 'submit',
            className: 'e-button e-button--gray',
            title: f({ id: 'gdprProfile.saveProfile' }),
            submitCallback: handleSubmit(onSubmit),
          },
        ]
      : [
          {
            id: 'gdpr-profile-form-close',
            type: 'button',
            className: 'e-button',
            title: f({ id: 'suppliersForm.close' }),
            closeCallback: () => {
              trackEvent(['GDPR profile form', 'click', 'Close subpage']);
              props.closeCallback();
            }
          },
        ];

  const getIsEditable = () => {
    if (props.isEdited && !props.customerId) {
      return false;
    }
    return true;
  };

  return (
    <CmdSubpage
      id="gdpr-profile-form"
      title={f({ id: 'menu.gdprProfile' })}
      subtitle={props.editingItemId ? f({ id: 'table.detail' }) : f({ id: 'table.add' })}
      buttons={subpageButtons}
      loader={loader}
      maintitle={f({ id: 'menu.gdpr' })}
      onlineHelp={<OnlineHelp topic={!props.isEdited ? 'add-gdpr-profile' : 'gdpr-profiles'} />}
    >
      <div className="row">
        <div className="col-xxl-12">
          <CmdBox
            id="alarms-basic-information"
            title={f({ id: 'alarms.section.basicInformation' })}
          >
            <div className="row">
              <div className="col-xxl-6">
                {props.editingItemId ? (
                  <CmdLabel title={f({ id: 'form.title' })} value={editingData?.name} />
                ) : (
                  <Controller
                    name="name"
                    control={control}
                    rules={{ required: errors.name ? errors.name.message : undefined }}
                    render={({ field }) => (
                      <CmdInput
                        {...field}
                        label={f({ id: 'form.title' })}
                        id="name"
                        name="name"
                        type="text"
                        placeholder={f({ id: 'gdprProfile.name.placeholder' })}
                        required={true}
                        onChangeCallback={(value) => {
                          setValue('name', value);
                          trigger('name');
                        }}
                        tooltip={f({ id: 'alarms.modal.recipient.formField.email.tooltip' })}
                        error={errors.name?.message}
                        touched={true}
                      />
                    )}
                  />
                )}
                {(props.editingItemId && editingData && editingData.customerId) ||
                (!editingData && customerOptions && customerOptions.length === 1) ? (
                  <CmdLabel title={f({ id: 'periodLock.customerId' })} value={getCustomerLabel()} />
                ) : (
                  !props.editingItemId &&
                  customerOptions &&
                  customerOptions.length > 1 && (
                    <Controller
                      name="customerId"
                      control={control}
                      rules={{
                        required: errors.customerId ? errors.customerId.message : undefined,
                      }}
                      render={({ field }) => (
                        <CmdSimpleSelect
                          {...field}
                          id="customerId"
                          name="customerId"
                          label={f({ id: 'periodLock.customerId' })}
                          introOption={f({ id: 'suppliersForm.choose' })}
                          options={customerOptions}
                          required
                          onChangeCallback={(value: string) => {
                            trackEvent(['GDPR profile form', 'change', 'Customer', Number(value)]);
                            setValue('customerId', value);
                            trigger('customerId');
                          }}
                          error={errors.customerId?.message}
                          touched={true}
                        />
                      )}
                    />
                  )
                )}
              </div>
              <div className="col-xxl-6">
                {props.editingItemId && !props.customerId ? (
                  <CmdLabel title={f({ id: 'vehicle.note' })} value={editingData?.description} />
                ) : (
                  <Controller
                    name="description"
                    control={control}
                    rules={{
                      required: errors.description ? errors.description.message : undefined,
                    }}
                    render={({ field }) => (
                      <CmdTextarea
                        {...field}
                        id="description"
                        name="description"
                        label={f({ id: 'vehicle.note' })}
                        disabled={!!(props.editingItemId && !props.isEdited)}
                        setValue={(value) => {
                          setValue('description', value);
                          trigger('description');
                        }}
                        error={errors.description?.message}
                        touched={true}
                      />
                    )}
                  />
                )}
              </div>
            </div>
          </CmdBox>
        </div>
      </div>

      {(!props.editingItemId || props.customerId) && (
        <div className="row">
          <div className="col-xxl-12">
            <div
              style={{
                paddingBottom: '16px',
                marginLeft: '-16px',
                fontSize: '16px',
              }}
            >
              <strong>{f({ id: 'gdprProfile.setting' })}</strong>
            </div>
            <div style={{ marginBottom: '24px' }}>
              <CmdAlert
                data={{
                  title: f({ id: 'gdprProfile.warning' }),
                  type: 'error',
                  message: f({ id: 'gdprProfile.message' }),
                }}
                id="gdpr-profile-alert"
                noCancel={true}
              />
            </div>
          </div>
        </div>
      )}

      <div className="row">
        <div className="col-xxl-6">
          <CmdBox
            id="alarms-basic-information"
            title={f({ id: 'menu.map' })}
            label={f({ id: 'gdprProfile.section.label.map' })}
          >
            <Controller
              name="map"
              control={control}
              render={({ field }) => (
                <CmdSwitch
                  {...field}
                  id="map"
                  label={f({ id: 'gdprProfile.map' })}
                  isRight={true}
                  disabled={isDisabledSwitch}
                  setValue={(value) => {
                    if (value === '1') {
                      trackEvent(['GDPR profile form', 'change', 'Show actual vehicle position on map enabled']);
                    }
                    if (value === '0') {
                      trackEvent(['GDPR profile form', 'change', 'Show actual vehicle position on map disabled']);
                    }
                    setValue('map', value);
                    trigger('map');
                  }}
                />
              )}
            />
          </CmdBox>
          <CmdBox
            id="alarms-basic-information"
            title={f({ id: 'menu.reports' })}
            label={f({ id: 'gdprProfile.section.label.reports' })}
          >
            <Controller
              name="R01"
              control={control}
              render={({ field }) => (
                <CmdSwitch
                  {...field}
                  id="R01"
                  label={f({ id: 'gdprProfile.R01' })}
                  isRight={true}
                  disabled={isDisabledSwitch}
                  setValue={(value) => {
                    if (value === '1') {
                      trackEvent(['GDPR profile form', 'change', 'R01 enabled']);
                    }
                    if (value === '0') {
                      trackEvent(['GDPR profile form', 'change', 'R01 disabled']);
                    }
                    setValue('R01', value);
                    trigger('R01');
                  }}
                />
              )}
            />

            <Controller
              name="R03"
              control={control}
              render={({ field }) => (
                <CmdSwitch
                  {...field}
                  id="R03"
                  label={f({ id: 'gdprProfile.R03' })}
                  isRight={true}
                  disabled={isDisabledSwitch}
                  setValue={(value) => {
                    if (value === '1') {
                      trackEvent(['GDPR profile form', 'change', 'R03 enabled']);
                    }
                    if (value === '0') {
                      trackEvent(['GDPR profile form', 'change', 'R03 disabled']);
                    }
                    setValue('R03', value);
                    trigger('R03');
                  }}
                />
              )}
            />

            <Controller
              name="R05"
              control={control}
              render={({ field }) => (
                <CmdSwitch
                  {...field}
                  id="R01"
                  label={f({ id: 'gdprProfile.R05' })}
                  isRight={true}
                  disabled={isDisabledSwitch}
                  setValue={(value) => {
                    if (value === '1') {
                      trackEvent(['GDPR profile form', 'change', 'R05 enabled']);
                    }
                    if (value === '0') {
                      trackEvent(['GDPR profile form', 'change', 'R05 disabled']);
                    }
                    setValue('R05', value);
                    trigger('R05');
                  }}
                />
              )}
            />
            <Controller
              name="R05Detail"
              control={control}
              rules={{
                required: errors.R05Detail ? errors.R05Detail.message : undefined,
              }}
              render={({ field }) => (
                <CmdCheckboxGroup
                  {...field}
                  id="R05Detail"
                  name="R05Detail"
                  label=""
                  tooltip={f({ id: 'gdprProfile.R05.tooltip' })}
                  options={[
                    {
                      value: 'showPrivateData',
                      item: f({ id: 'gdprProfile.showPrivateData' }),
                    },
                  ]}
                  disabled={!!(!getIsEditable() || String(watch('R05')) !== '1')}
                  gridTemplateColumns=" 1fr"
                  setValue={(value) => {
                    if (value === '1') {
                      trackEvent(['GDPR profile form', 'change', 'R05 detail enabled']);
                    }
                    if (value === '0') {
                      trackEvent(['GDPR profile form', 'change', 'R05 detail disabled']);
                    }
                    setValue('R05Detail', value);
                    trigger('R05Detail');
                  }}
                />
              )}
            />

            <Controller
              name="R06"
              control={control}
              render={({ field }) => (
                <CmdSwitch
                  {...field}
                  id="R06"
                  noMargin
                  label={f({ id: 'gdprProfile.R06' })}
                  isRight={true}
                  disabled={isDisabledSwitch}
                  setValue={(value) => {
                    if (value === '1') {
                      trackEvent(['GDPR profile form', 'change', 'R06 enabled']);
                    }
                    if (value === '0') {
                      trackEvent(['GDPR profile form', 'change', 'R06 disabled']);
                    }
                    setValue('R06', value);
                    trigger('R06');
                  }}
                />
              )}
            />

            <Controller
              name="R07"
              control={control}
              render={({ field }) => (
                <CmdSwitch
                  {...field}
                  id="R07"
                  label={f({ id: 'gdprProfile.R07' })}
                  isRight={true}
                  disabled={isDisabledSwitch}
                  setValue={(value) => {
                    if (value === '1') {
                      trackEvent(['GDPR profile form', 'change', 'R07 enabled']);
                    }
                    if (value === '0') {
                      trackEvent(['GDPR profile form', 'change', 'R07 disabled']);
                    }
                    setValue('R07', value);
                    trigger('R07');
                  }}
                />
              )}
            />
          </CmdBox>
          <CmdBox
            id="alarms-basic-information"
            title={f({ id: 'menu.corrections' })}
            label={f({ id: 'gdprProfile.section.label.correction' })}
          >
            <Controller
              name="correction"
              control={control}
              render={({ field }) => (
                <CmdSwitch
                  {...field}
                  id="correction"
                  label={f({ id: 'gdprProfile.correction' })}
                  isRight={true}
                  disabled={isDisabledSwitch}
                  setValue={(value) => {
                    if (value === '1') {
                      trackEvent(['GDPR profile form', 'change', 'Show address is enabled']);
                    }
                    if (value === '0') {
                      trackEvent(['GDPR profile form', 'change', 'Show address is disabled']);
                    }
                    setValue('correction', value);
                    trigger('correction');
                  }}
                />
              )}
            />
          </CmdBox>
        </div>

        <div className="col-xxl-6">
          <CmdBox
            id="alarms-basic-information"
            title={f({ id: 'menu.overviewTours' })}
            label={f({ id: 'gdprProfile.section.label.rides' })}
          >
            <Controller
              name="ride"
              control={control}
              render={({ field }) => (
                <CmdSwitch
                  {...field}
                  id="ride"
                  label={f({ id: 'gdprProfile.ride' })}
                  isRight={true}
                  disabled={isDisabledSwitch}
                  setValue={(value) => {
                    if (value === '1') {
                      trackEvent(['GDPR profile form', 'change', 'Ride enabled']);
                    }
                    if (value === '0') {
                      trackEvent(['GDPR profile form', 'change', 'Ride disabled']);
                    }
                    setValue('ride', value);
                    trigger('ride');
                  }}
                />
              )}
            />

            <Controller
              name="rideDetail"
              control={control}
              rules={{
                required: errors.rideDetail ? errors.rideDetail.message : undefined,
              }}
              render={({ field }) => (
                <CmdCheckboxGroup
                  {...field}
                  id="rideDetail-1"
                  name="rideDetail"
                  label=""
                  options={[
                    {
                      value: 'showPrivateData',
                      item: f({ id: 'gdprProfile.showPrivateData' }),
                      tooltip: f({ id: 'gdprProfile.showPrivateData.tooltip' }),
                    },
                  ]}
                  disabled={!!(!getIsEditable() || String(watch('ride')) !== '1')}
                  gridTemplateColumns=" 1fr"
                  setValue={(value) => {
                    if (value.includes('showPrivateData')) {
                      trackEvent(['GDPR profile form', 'change', 'Rides - show private data enabled']);
                    } else {
                      trackEvent(['GDPR profile form', 'change', 'Rides - show private data disabled']);
                    }
                    setValue('rideDetail', value);
                    trigger('rideDetail');
                  }}
                />
              )}
            />

            <div style={{ marginLeft: '25px', marginTop: '-25px' }}>
              <Controller
                name="rideDetail"
                control={control}
                rules={{
                  required: errors.rideDetail ? errors.rideDetail.message : undefined,
                }}
                render={({ field }) => (
                  <CmdCheckboxGroup
                    {...field}
                    id="rideDetail-2"
                    name="rideDetail"
                    label=""
                    options={[
                      { value: 'showEvents', item: f({ id: 'gdprProfile.showEvents' }) },
                      { value: 'showAlarms', item: f({ id: 'gdprProfile.showAlarms' }) },
                      { value: 'showWaypoints', item: f({ id: 'gdprProfile.showWaypoints' }) },
                    ]}
                    disabled={
                      !!(
                        !getIsEditable() ||
                        String(watch('ride')) !== '1' ||
                        !watch('rideDetail').includes('showPrivateData')
                      )
                    }
                    gridTemplateColumns=" 1fr"
                    setValue={(value) => {
                      if (value.includes('showAlarms')) {
                        trackEvent(['GDPR profile form', 'change', 'Rides - show alarms enabled']);
                      }
                      if (value.includes('showWaypoints')) {
                        trackEvent(['GDPR profile form', 'change', 'Rides - show waypoints enabled']);
                      }
                      if (value.includes('showEvents')) {
                        trackEvent(['GDPR profile form', 'change', 'Rides - show events enabled']);
                      }
                      setValue('rideDetail', value);
                      trigger('rideDetail');
                    }}
                  />
                )}
              />
            </div>
            <p
              style={{
                marginLeft: '70px',
                fontWeight: 'bold',
                fontSize: '11px',
                color: 'rgb(179, 179, 179)',
                marginTop: '-10px',
              }}
            >
              {f({ id: 'gdprProfile.tripsOverview.note' })}
            </p>
            <div style={{ marginLeft: '25px' }}>
              <Controller
                name="rideDetail"
                control={control}
                rules={{
                  required: errors.rideDetail ? errors.rideDetail.message : undefined,
                }}
                render={({ field }) => (
                  <CmdCheckboxGroup
                    {...field}
                    id="rideDetail-3"
                    name="rideDetail"
                    label=""
                    options={[
                      {
                        value: 'showRideDetail',
                        item: f({ id: 'gdprProfile.showRideDetail' }),
                      },
                      { value: 'showRide', item: f({ id: 'gdprProfile.showRide' }) },
                    ]}
                    disabled={
                      !!(
                        !getIsEditable() ||
                        String(watch('ride')) !== '1' ||
                        !watch('rideDetail').includes('showPrivateData')
                      )
                    }
                    gridTemplateColumns=" 1fr"
                    setValue={(value) => {
                      if (value.includes('showRideDetail')) {
                        trackEvent(['GDPR profile form', 'change', 'Rides - show vehicle positions enabled']);
                      }
                      if (value.includes('showRide')) {
                        trackEvent(['GDPR profile form', 'change', 'Rides - show ride enabled']);
                      }
                      setValue('rideDetail', value);
                      trigger('rideDetail');
                    }}
                  />
                )}
              />
            </div>

            <Controller
              name="rideDetail"
              control={control}
              rules={{
                required: errors.rideDetail ? errors.rideDetail.message : undefined,
              }}
              render={({ field }) => (
                <CmdCheckboxGroup
                  {...field}
                  id="rideDetail"
                  name="rideDetail"
                  label=""
                  options={[
                    { value: 'changeDriver', item: f({ id: 'gdprProfile.changeDriver' }) },
                    {
                      value: 'changeRidePrivacy',
                      item: f({ id: 'gdprProfile.changeRidePrivacy' }),
                    },
                  ]}
                  disabled={!!(!getIsEditable() || String(watch('ride')) !== '1')}
                  gridTemplateColumns=" 1fr"
                  setValue={(value) => {
                    if (value.includes('changeDriver')) {
                      trackEvent(['GDPR profile form', 'change', 'Rides - change driver enabled']);
                    }
                    if (value.includes('changeRidePrivacy')) {
                      trackEvent(['GDPR profile form', 'change', 'Rides - changed ride privacy enabled']);
                    }
                    setValue('rideDetail', value);
                    trigger('rideDetail');
                  }}
                />
              )}
            />
          </CmdBox>
          <CmdBox
            id="alarms-basic-information"
            title={f({ id: 'menu.refuelings' })}
            label={f({ id: 'gdprProfile.section.label.refueling' })}
          >
            <Controller
              name="refueling"
              control={control}
              render={({ field }) => (
                <CmdSwitch
                  {...field}
                  id="refueling"
                  label={f({ id: 'gdprProfile.refueling' })}
                  isRight={true}
                  disabled={isDisabledSwitch}
                  setValue={(value) => {
                    if (value === '1') {
                      trackEvent(['GDPR profile form', 'change', 'Refuelings enabled']);
                    } else {
                      trackEvent(['GDPR profile form', 'change', 'Refuelings disabled']);
                    }
                    setValue('refueling', value);
                    trigger('refueling');
                  }}
                />
              )}
            />
          </CmdBox>
        </div>
      </div>
      {props.usersCount !== undefined &&
        props.editingItemId &&
        props.customerId &&
        dataReadyToSubmit && (
          <ConfirmDialog
            title={f({ id: 'periodLock.saveRecord' })}
            cancelCallback={handleConfirmCancelAction}
            continueCallback={handleConfirmSubmitAction}
            cancelButtonTitle={f({ id: 'suppliersForm.close' })}
            continueButtonTitle={f({ id: 'suppliersForm.save' })}
          >
            {f({ id: 'gdprProfile.confirm.save.message' }, { _count: props.usersCount })}
          </ConfirmDialog>
        )}
      {isCancelButtonClicked && (
        <ConfirmDialog
          title={f({ id: 'table.confirm.dialog.onlineTemplates.subpage.close.title' })}
          cancelCallback={handleConfirmKeepSubpage}
          continueCallback={handleConfirmCancelSubpage}
          cancelButtonTitle={f({ id: 'confirm.dialog.answerNo' })}
          continueButtonTitle={f({ id: 'confirm.dialog.answerYes' })}
        >
          {f({ id: 'table.confirm.dialog.onlineTemplates.subpage.close.message' })}
        </ConfirmDialog>
      )}
    </CmdSubpage>
  );
}
